// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { ReportGeneration as Constants, Routes, DateTimeFormats } from "constants/global";
import { getConfigData, getConfigSelector, getLoggedInUserInfo } from "redux/userList/userSlice";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// types
import { ConfigDataItem } from "services/api/types";
import { Users } from "redux/types";
import { getTenantReimbSettlementReport, getTenantVouchRedemptionReport } from "services/api/reimbursement";
import { downloadFile } from "utils/downloadFile";

// utils
import { isFromCalenderError, isToCalenderError } from "utils/CalenderError";

type ReportType = {
	type: string | null;
	selected: boolean;
};

type VoucherType = {
	type: string;
	selected: boolean;
};

type TenantReimbSettlementReportPayload = {
	from_year_month: string;
	to_year_month: string;
	outlets: string[];
	voucher_group_code: string;
};

type TenantVouRedemptionReportPayload = {
	from_date: string;
	to_date: string;
	outlets: string[];
	voucher_group_code: string;
};

type Outlet = {
	outlet_code: string;
	outlet_name: string;
	sm_store_id: string;
	terminal: string;
};

export function useReportGenerationTenant() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const configDetails = useAppSelector(getConfigSelector);
	const userInfo: Users["user"] = useAppSelector(getLoggedInUserInfo);
	const { outlets } = userInfo?.data?.[0] ?? { outlets: [] };
	const { data: configDetailsData } = configDetails || {};

	const [loading, setLoading] = useState(false);

	const [reportType, setReportType] = useState<ReportType>({ type: null, selected: false });
	const [voucherType, setVoucherType] = useState<VoucherType>({ type: "", selected: false });

	const [fromMonth, setFromMonth] = useState<string | null>(null);
	const [fromYear, setFromYear] = useState<number | null>(null);

	const [toMonth, setToMonth] = useState<string | null>(null);
	const [toYear, setToYear] = useState<string | null>(null);

	const [searchSelected, setSearchSelected] = useState<boolean>(false);
	const [searchInput, setSearchInput] = useState<string | null>(null);

	const [selectedValue, setSelectedValue] = useState<string | null>(null);
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const [voucherRedemptionStartDate, setVoucherRedemptionStartDate] = useState<string>("");
	const [voucherRedemptionEndDate, setVoucherRedemptionEndDate] = useState<string>("");
	const [dateRangeError, setDateRangeError] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getConfigData({ config_mst_code: Constants.CONFIG_MASTER_CODE_VOUCHER_TYPES }));
	}, []);

	const voucherTypes = useMemo(() => {
		return (configDetailsData || [])?.map((_config: ConfigDataItem) => ({
			id: _config.config_dtl_code,
			value: _config.config_dtl_code,
			label: _config.config_dtl_desc
		}));
	}, [configDetailsData]);

	const onChange = (value: string) => {
		if (value) {
			const selectVal = value.split("*");
			setSelectedValue(selectVal[1]);
			setSearchInput(selectVal[0]);
			setSearchSelected(true);
		} else {
			setSelectedValue(null);
			setSearchInput(null);
			setSearchSelected(false);
		}
	};
	const onSearch = (value: string) => {
		if (value) {
			setSearchInput(value);
			setSearchSelected(true);
		} else {
			setSearchInput(null);
			setSearchSelected(false);
		}
	};
	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSearchInput(null);
			setSelectedValue(null);
			setSelectAll(checked);
			setSearchSelected(true);
		} else {
			setSelectAll(checked);
			setSearchSelected(false);
			setReportType({ ...reportType, type: null, selected: false });
			setVoucherType({ ...voucherType, type: "", selected: false });
			setFromMonth(null);
			setFromYear(null);
		}
	};

	const handleReportType = useCallback(
		(value: string) => {
			setReportType({ ...reportType, type: value, selected: true });
		},
		[reportType, setReportType]
	);

	const handleVoucherType = useCallback(
		(value: string) => {
			setVoucherType({ ...voucherType, type: value, selected: true });
		},
		[voucherType, setVoucherType]
	);

	const handleFromMonth = useCallback(
		(value: string) => {
			setFromMonth(value);
		},
		[fromMonth, setFromMonth]
	);

	const handleFromYear = useCallback(
		(value: number | null) => {
			setFromYear(value);
		},
		[fromYear, setFromYear]
	);

	const handleVoucherRedemptionDate = useCallback(
		(_type: string, _date: string) => {
			if (_type === Constants.DATE_TYPE.START_DATE) {
				setVoucherRedemptionStartDate(_date);
			}

			if (_type === Constants.DATE_TYPE.END_DATE) {
				if (dateRangeError) {
					setDateRangeError(false);
				}
				const startDate = moment();
				const endDate = moment(_date);

				if (startDate < endDate) {
					setDateRangeError(true);
				}
				setVoucherRedemptionEndDate(_date);
			}
		},
		[voucherRedemptionEndDate, voucherRedemptionStartDate]
	);

	const toError = useMemo(() => {
		return isToCalenderError(fromYear, toMonth, fromMonth);
	}, [fromYear, toMonth, fromMonth, isToCalenderError]);

	const fromError = useMemo(() => {
		return isFromCalenderError(fromYear, fromMonth);
	}, [fromMonth, fromYear, isFromCalenderError]);

	const handleToMonth = useCallback(
		(value: string) => {
			setToMonth(value);
		},
		[toMonth, setToMonth]
	);

	const handleToYear = useCallback(
		(value: string) => {
			setToYear(value);
		},
		[toYear, setToYear]
	);

	const isDownloadButtonEnabled = useCallback(() => {
		if (reportType?.type === Constants.SETTLEMENT_REPORT_TYPE) {
			return !!(fromMonth && fromYear && reportType && !fromError && !toError);
		}

		if (reportType?.type === Constants.REPORT_TYPE[1].value) {
			return !!(!dateRangeError && voucherRedemptionStartDate && reportType.selected);
		}
		return false;
	}, [fromMonth, fromYear, reportType, fromError, toError, dateRangeError, voucherRedemptionStartDate, voucherRedemptionEndDate]);

	const onSuccess = () => {
		navigate(Routes.REPORTS_TENANT);
	};

	const resetAll = () => {
		setReportType({ ...reportType, type: null, selected: false });
		setSelectAll(false);
		setSearchInput(null);
		setVoucherType({ ...voucherType, type: "", selected: false });
		setFromMonth(null);
		setFromYear(null);
		setToMonth(null);
		setToYear(null);
		setSelectedValue("");
		setSearchSelected(false);
		setDateRangeError(false);
		setVoucherRedemptionStartDate("");
		setVoucherRedemptionEndDate("");
	};

	const generateSettlementReportPayload = useCallback(() => {
		const outletsVal = selectAll ? outlets?.map((outlet: Outlet) => outlet?.outlet_code) : [selectedValue];
		const payload: TenantReimbSettlementReportPayload = {
			from_year_month: `${fromYear}${fromMonth}`,
			to_year_month: `${toYear || fromYear}${toMonth || fromMonth}`,
			outlets: outlets.length > 1 ? outletsVal : [`${outlets[0]?.outlet_code}`],
			voucher_group_code: voucherType?.type
		};
		return payload;
	}, [reportType, fromYear, fromMonth, toYear, toMonth, outlets, selectAll, selectedValue, voucherType]);

	const generateRedemptionReportPayload = useCallback(() => {
		const outletsVal = selectAll ? outlets?.map((outlet: Outlet) => outlet?.outlet_code) : [selectedValue];
		const payload: TenantVouRedemptionReportPayload = {
			from_date: `${moment(voucherRedemptionStartDate).format(DateTimeFormats.USE_YYYYMMDD)}`,
			to_date: `${voucherRedemptionStartDate && voucherRedemptionEndDate ? moment(voucherRedemptionEndDate).format(DateTimeFormats.USE_YYYYMMDD) : ""}`,
			outlets: outlets.length > 1 ? outletsVal : [`${outlets[0]?.outlet_code}`],
			voucher_group_code: voucherType?.type
		};
		return payload;
	}, [reportType, voucherRedemptionStartDate, voucherRedemptionEndDate, outlets, selectAll, selectedValue, voucherType]);

	const onFailure = useCallback(() => {
		setLoading(false);
		CountDownModal({
			timer: 3,
			errorIcon: true,
			title: Constants.NO_DATA_FOUND,
			onTimeComplete: onSuccess
		});
	}, []);

	/**
	 * @desc Pending implementation for Voucher Redemption Report
	 */
	const proceedToGenerateReport = useCallback(async () => {
		if (isDownloadButtonEnabled()) {
			setLoading(true);

			if (reportType?.selected) {
				try {
					const { data: reportsData } =
						reportType?.type === Constants.SETTLEMENT_REPORT_TYPE
							? await getTenantReimbSettlementReport(generateSettlementReportPayload())
							: await getTenantVouchRedemptionReport(generateRedemptionReportPayload());

					const { data, errors } = reportsData;
					if (data?.file_url && !errors?.length) {
						setLoading(false);
						try {
							await downloadFile(data?.file_url);

							CountDownModal({
								timer: 3,
								title: Constants.REPORT_GENERATED,
								onTimeComplete: onSuccess
							});
						} catch (error) {
							onFailure();
						}
					} else if (errors?.length) {
						onFailure();
					}
				} catch (error) {
					onFailure();
				}
			}

			resetAll();
		}
	}, [reportType, generateSettlementReportPayload, isDownloadButtonEnabled]);

	return {
		searchSelected,
		searchInput,
		selectAll,
		loading,
		selectedValue,
		onSearch,
		onChange,
		reportType,
		voucherType,
		fromMonth,
		fromYear,
		toMonth,
		toYear,
		voucherTypes,
		resetAll,
		fromError,
		toError,
		voucherRedemptionStartDate,
		voucherRedemptionEndDate,
		dateRangeError,
		setSelectAll,
		setSelectedValue,
		handleFromMonth,
		handleFromYear,
		handleToMonth,
		handleToYear,
		onSuccess,
		setSearchSelected,
		handleReportType,
		handleVoucherType,
		isDownloadButtonEnabled,
		handleSelectAll,
		proceedToGenerateReport,
		handleVoucherRedemptionDate
	};
}
